import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Stars } from "@/components/site/ProductCard";
import { Reveal } from "@/components/site/Reveal";
import { toast } from "sonner";

export default function Reviews() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [productId, setProductId] = useState("");
  const [sort, setSort] = useState("newest");
  const [form, setForm] = useState({ product_id: "", rating: 5, display_name: "", text: "" });
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const productNames = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);

  const visible = useMemo(() => {
    let list = [...reviews];
    if (productId) list = list.filter((r) => r.product_id === productId);
    if (sort === "highest") list.sort((a, b) => b.rating - a.rating);
    else if (sort === "lowest") list.sort((a, b) => a.rating - b.rating);
    else list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [reviews, productId, sort]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) { toast.error("Please choose a fragrance to review."); return; }
    setSending(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you — your review has been submitted for moderation.");
      setForm({ product_id: "", rating: 5, display_name: "", text: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  return (
    <div className="pt-20" data-testid="reviews-page">
      <div className="bg-[#0A0A0A] gold-hairline-b py-16 sm:py-24 relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <Reveal>
            <p className="eyebrow mb-4">In Their Words</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white font-light" data-testid="reviews-title">
              Customer Reviews
            </h1>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16">
        <div className="flex flex-wrap gap-3 items-center pb-10 border-b border-[#D4AF37]/10">
          <select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="reviews-filter-product"
            className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
            <option value="">All Fragrances</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="reviews-sort-select"
            className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
            <option value="newest">Newest</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {visible.length === 0 && (
              <div className="md:col-span-2 gold-hairline bg-[#0D0D0D] p-12 text-center" data-testid="reviews-empty">
                <p className="font-serif-display text-2xl text-white">No reviews yet.</p>
                <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-3">
                  Be the first to share your experience with Kubwalo Fragrances.
                </p>
              </div>
            )}
            {visible.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.08 }} className="gold-hairline bg-[#0D0D0D] p-6" data-testid={`reviews-card-${r.id}`}>
                <Stars rating={r.rating} />
                <p className="font-sans-body text-sm text-white mt-4 leading-relaxed">{r.text}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="font-sans-body text-[11px] uppercase tracking-[0.2em] text-[#C6A15B]">
                    {r.display_name}{r.verified_purchase && <span className="text-[#D4AF37]"> · Verified</span>}
                  </span>
                  <span className="font-sans-body text-[10px] text-[#666666]">
                    {productNames[r.product_id] || "Fragrance"}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <form onSubmit={submit} className="gold-hairline bg-[#0D0D0D] p-6 space-y-4 h-fit" data-testid="reviews-page-form">
            <h3 className="font-serif-display text-2xl text-white">Leave a Review</h3>
            <select required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="reviews-form-product"
              className="w-full bg-[#080808] border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37]">
              <option value="">Choose a fragrance...</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} aria-label={`Rate ${i}`} data-testid={`reviews-form-star-${i}`}>
                  <Star size={20} className={i <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3a3a3a]"} />
                </button>
              ))}
            </div>
            <input required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="Display name" data-testid="reviews-form-name"
              className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37]" />
            <textarea required minLength={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Your review" rows={4} data-testid="reviews-form-text"
              className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] resize-none" />
            <button type="submit" className="btn-gold w-full" disabled={sending} data-testid="reviews-form-submit">
              {sending ? "Submitting..." : "Submit for Review"}
            </button>
            <p className="font-sans-body text-[11px] text-[#666666] font-light">
              All reviews are moderated before they appear publicly.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
